import { useCallback, useEffect, useState } from "react";
import type { BookingSubmissionResult } from "../../utils/apiClient";

export type WizardStep = 1 | 2 | 3;

export interface BookingFormValues {
  fullName: string;
  email: string;
  phone: string;
  startTime: string;
  endTime: string;
  endNextDay: boolean;
  location: string;
  eventType: string;
  eventTypeOther: string;
  guestCount: string;
  customPrintDesign: string;
  comments: string;
}

export const EMPTY_FORM_VALUES: BookingFormValues = {
  fullName: "",
  email: "",
  phone: "",
  startTime: "",
  endTime: "",
  endNextDay: false,
  location: "",
  eventType: "",
  eventTypeOther: "",
  guestCount: "",
  customPrintDesign: "",
  comments: "",
};

const SESSION_STORAGE_KEY = "dmm_booking_confirmation_v1";

/**
 * Alleen deze niet-gevoelige samenvattingsvelden (exact wat stap 3 al toont)
 * overleven een paginaverversing, in sessionStorage (niet localStorage, en
 * verdwijnt zodra het tabblad sluit). Naam, e-mailadres, telefoonnummer,
 * opmerkingen en andere ingevulde persoonsgegevens worden nooit opgeslagen -
 * die bestaan uitsluitend in het React-geheugen tijdens het invullen en
 * worden na een succesvolle inzending direct gewist.
 */
interface StoredConfirmation {
  confirmation: BookingSubmissionResult;
}

function readStoredConfirmation(): StoredConfirmation | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConfirmation;
  } catch {
    return null;
  }
}

export function useBookingState() {
  const [step, setStep] = useState<WizardStep>(() => (readStoredConfirmation() ? 3 : 1));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<BookingFormValues>(EMPTY_FORM_VALUES);
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => crypto.randomUUID());
  const [confirmation, setConfirmation] = useState<BookingSubmissionResult | null>(
    () => readStoredConfirmation()?.confirmation ?? null,
  );

  useEffect(() => {
    if (confirmation) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ confirmation }));
    }
  }, [confirmation]);

  const goToStep2 = useCallback(() => {
    setIdempotencyKey(crypto.randomUUID());
    setStep(2);
  }, []);

  const goBackToStep1 = useCallback(() => {
    setStep(1);
  }, []);

  const completeWithConfirmation = useCallback((result: BookingSubmissionResult) => {
    setConfirmation(result);
    setStep(3);
    // Persoonsgegevens uit de browserstate verwijderen na een succesvolle inzending.
    setFormValues(EMPTY_FORM_VALUES);
  }, []);

  return {
    step,
    selectedDate,
    setSelectedDate,
    formValues,
    setFormValues,
    idempotencyKey,
    confirmation,
    goToStep2,
    goBackToStep1,
    completeWithConfirmation,
  };
}
